import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    Radio,
    styled,
    useTheme,
    alpha,
    IconButton,
} from "@mui/material";
import { Box } from "@mui/system";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import Text from "components/mui/Text";
import AppButtonComponent from "components/mui/AppButtonComponent";

// ─── Styled Components ──────────────────────────────────────────────────────────

const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialog-paper": {
        background: "#13172A",
        borderRadius: "16px",
        color: "#fff",
        maxWidth: 660,
        width: "100%",
        margin: theme.spacing(2),
        maxHeight: "calc(100vh - 40px)",
        display: "flex",
        flexDirection: "column",
    },
    "& .MuiBackdrop-root": {
        backgroundColor: "rgba(0, 0, 0, 0.65)",
        backdropFilter: "blur(8px)",
    },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: theme.spacing(2.5, 3),
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    "&:first-of-type": { paddingTop: theme.spacing(2.5) },
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(2),
    },
}));

const OptionCard = styled(Box, {
    shouldForwardProp: (prop) => prop !== "selected",
})(({ theme, selected }) => ({
    border: `1.5px solid ${selected ? theme.palette.primary.main : "rgba(255,255,255,0.12)"}`,
    borderRadius: 10,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    cursor: "pointer",
    transition: "border-color 0.2s ease, background 0.2s ease",
    background: selected ? alpha(theme.palette.primary.main, 0.07) : "transparent",
    "&:hover": {
        borderColor: selected ? theme.palette.primary.main : "rgba(255,255,255,0.28)",
    },
}));

const CheckItem = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: 7,
    marginTop: theme.spacing(0.8),
}));

const BadgeRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: 5,
    marginTop: theme.spacing(1.2),
    paddingTop: theme.spacing(1.2),
    borderTop: "1px solid rgba(255,255,255,0.08)",
}));

// ─── Main Component ─────────────────────────────────────────────────────────────

const ChooseExperienceModal = ({ open = false, onClose, onContinue, userName }) => {
    const theme = useTheme();
    const [selected, setSelected] = useState("fee-based");

    const handleContinue = () => {
        if (onContinue) onContinue(selected);
        else onClose(selected);
    };

    return (
        <StyledDialog
            open={open}
            onClose={() => { }} // ignore backdrop click closing
            disableEscapeKeyDown
            maxWidth="md"
        >
            {/* <IconButton
                onClick={onClose}
                sx={{
                    position: 'absolute',
                    right: 12,
                    top: 12,
                    color: 'rgba(255,255,255,0.6)',
                    '&:hover': { color: '#fff' }
                }}
            >
                <CloseIcon />
            </IconButton> */}
            <StyledDialogContent>

                {/* ── Title ── */}
                <Box sx={{ textAlign: "center", mb: 3 }}>
                    <Text sx={{
                        fontWeight: 700,
                        fontSize: { xs: "20px", sm: "22px" },
                        lineHeight: 1.3,
                        color: "#fff",
                        mb: 0.5
                    }}>
                        Welcome, {userName || "Investor"}!
                    </Text>
                    <Text sx={{
                        fontWeight: 400,
                        fontSize: { xs: "13px", sm: "14px" },
                        color: "rgba(255, 255, 255, 0.6)"
                    }}>
                        Pick how you'd like to invest with inXits
                    </Text>
                </Box>

                {/* ── Option 1: Fee-based (Direct Plan) ── */}
                <OptionCard selected={selected === "fee-based"} onClick={() => setSelected("fee-based")}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Text sx={{ fontWeight: 700, fontSize: { xs: "15px", sm: "16px" }, color: "#fff" }}>
                                    Direct Plan
                                </Text>
                                <Box sx={{
                                    bgcolor: "#28C76F",
                                    color: "#000",
                                    fontSize: "9px",
                                    fontWeight: 800,
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: "4px",
                                    letterSpacing: "0.5px"
                                }}>
                                    RECOMMENDED
                                </Box>
                            </Box>
                            <Text sx={{ fontWeight: 400, fontSize: "11px", color: "rgba(255,255,255,0.5)", mt: 0.2 }}>
                                Advisory-led · Direct fund
                            </Text>
                        </Box>
                        <Radio
                            checked={selected === "fee-based"}
                            onChange={() => setSelected("fee-based")}
                            size="small"
                            sx={{
                                color: "rgba(255,255,255,0.3)",
                                "&.Mui-checked": { color: theme.palette.primary.main },
                                mt: -0.5, mr: -0.5, p: "6px",
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </Box>

                    {/* Price Tag */}
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mb: 2 }}>
                        <Text sx={{ fontWeight: 700, fontSize: "20px", color: "#fff" }}>
                            ₹399
                        </Text>
                        <Text sx={{ fontSize: "12px", color: "rgba(255,255,255,0.5)" }}>
                            /mo
                        </Text>
                    </Box>

                    <CheckItem>
                        <CheckCircleIcon sx={{ fontSize: 14, color: "#28C76F", mt: "2px", flexShrink: 0 }} />
                        <Text sx={{ fontSize: "12px", color: "rgba(255,255,255,0.85)" }}>
                            Access to Direct Funds — 0% distributor commission
                        </Text>
                    </CheckItem>

                    <CheckItem>
                        <CheckCircleIcon sx={{ fontSize: 14, color: "#28C76F", mt: "2px", flexShrink: 0 }} />
                        <Text sx={{ fontSize: "12px", color: "rgba(255,255,255,0.85)" }}>
                            More of your money stays invested & compounds over time
                        </Text>
                    </CheckItem>

                    <CheckItem>
                        <CheckCircleIcon sx={{ fontSize: 14, color: "#28C76F", mt: "2px", flexShrink: 0 }} />
                        <Text sx={{ fontSize: "12px", color: "rgba(255,255,255,0.85)" }}>
                            Expert, SEBI-registered investment advice
                        </Text>
                    </CheckItem>

                    <BadgeRow>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="11" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" />
                            <text x="12" y="16" textAnchor="middle" fontSize="9" fill="rgba(255,255,255,0.55)" fontFamily="Arial">S</text>
                        </svg>
                        <Text sx={{ fontSize: "10px", color: "rgba(255,255,255,0.4)" }}>
                            SEBI Registered · INA000020369
                        </Text>
                    </BadgeRow>
                </OptionCard>

                {/* ── Option 2: No-fee (Regular Plan) ── */}
                <OptionCard selected={selected === "no-fee"} onClick={() => setSelected("no-fee")}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Text sx={{ fontWeight: 700, fontSize: { xs: "15px", sm: "16px" }, color: "#fff" }}>
                                Regular Plan
                            </Text>
                            <Text sx={{ fontWeight: 400, fontSize: "11px", color: "rgba(255,255,255,0.5)", mt: 0.2 }}>
                                Commission-based · Regular funds
                            </Text>
                        </Box>
                        <Radio
                            checked={selected === "no-fee"}
                            onChange={() => setSelected("no-fee")}
                            size="small"
                            sx={{
                                color: "rgba(255,255,255,0.3)",
                                "&.Mui-checked": { color: theme.palette.primary.main },
                                mt: -0.5, mr: -0.5, p: "6px",
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </Box>

                    {/* Price Tag */}
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mb: 2 }}>
                        <Text sx={{ fontWeight: 700, fontSize: "20px", color: "#fff" }}>
                            ₹0
                        </Text>
                        <Text sx={{ fontSize: "12px", color: "rgba(255,255,255,0.5)" }}>
                            No subscription fee
                        </Text>
                    </Box>

                    <CheckItem>
                        <CheckCircleIcon sx={{ fontSize: 14, color: "#28C76F", mt: "2px", flexShrink: 0 }} />
                        <Text sx={{ fontSize: "12px", color: "rgba(255,255,255,0.85)" }}>
                            Access to Regular Funds
                        </Text>
                    </CheckItem>

                    <CheckItem>
                        <CheckCircleIcon sx={{ fontSize: 14, color: "#28C76F", mt: "2px", flexShrink: 0 }} />
                        <Box>
                            <Text sx={{ fontSize: "12px", color: "rgba(255,255,255,0.85)" }}>
                                A small SEBI-regulated commission goes to us from the fund house
                            </Text>
                            <Text sx={{ fontSize: "10px", color: "rgba(255,255,255,0.4)", mt: 0.2 }}>
                                Avg. Rs. 5 – 15 p.m. for every Rs 10000 market value.
                            </Text>
                        </Box>
                    </CheckItem>

                    <BadgeRow>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="11" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" />
                            <text x="12" y="16" textAnchor="middle" fontSize="9" fill="rgba(255,255,255,0.55)" fontFamily="Arial">A</text>
                        </svg>
                        <Text sx={{ fontSize: "10px", color: "rgba(255,255,255,0.4)" }}>
                            AMFI Registered · ARN-300310
                        </Text>
                    </BadgeRow>
                </OptionCard>

                {/* ── Continue Button ── */}
                <AppButtonComponent
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={handleContinue}
                    sx={{
                        mt: 2,
                        py: 1.5,
                        borderRadius: "10px",
                        fontWeight: 600,
                        fontSize: "14px",
                        textTransform: "none",
                        backgroundColor: theme.palette.primary.main,
                        "&:hover": { backgroundColor: theme.palette.primary.dark },
                    }}
                >
                    Continue
                </AppButtonComponent>

            </StyledDialogContent>
        </StyledDialog>
    );
};

export default ChooseExperienceModal;
